/**
 * Structural Load Manager
 * 
 * Manages load cases, load combinations, and load application.
 * Supports Dead, Live, Wind, Seismic, and Snow loads per building codes.
 */

import { v4 as uuidv4 } from 'uuid';

// ==================== Types ====================

export type LoadCaseType = 'Dead' | 'Live' | 'Wind' | 'Seismic' | 'Snow' | 'Rain' | 'Other';
export type LoadCombinationMethod = 'LRFD' | 'ASD' | 'Eurocode' | 'Custom';
export type LoadDirection = 'Gravity' | 'Lateral' | 'Uplift';

export interface PointLoad {
    id: string;
    nodeId: string;
    fx: number;  // kN
    fy: number;  // kN
    fz: number;  // kN
    mx: number;  // kN·m
    my: number;  // kN·m
    mz: number;  // kN·m
}

export interface DistributedLoad {
    id: string;
    memberId: string;
    startMagnitude: number;  // kN/m
    endMagnitude: number;    // kN/m (for trapezoidal)
    direction: 'Local' | 'Global';
    axis: 'X' | 'Y' | 'Z';
    startPosition: number;   // 0-1 along member
    endPosition: number;     // 0-1 along member
}

export interface AreaLoad {
    id: string;
    surfaceId: string;
    magnitude: number;  // kN/m²
    direction: 'Normal' | 'Global Z';
}

export interface LoadCase {
    id: string;
    name: string;
    type: LoadCaseType;
    selfWeightMultiplier: number;  // 0 = no self-weight, 1 = include
    pointLoads: PointLoad[];
    distributedLoads: DistributedLoad[];
    areaLoads: AreaLoad[];
}

export interface LoadCombination {
    id: string;
    name: string;
    method: LoadCombinationMethod;
    factors: Map<string, number>;  // LoadCase ID -> Factor
    isEnvelope: boolean;
}

// ==================== Standard Load Combinations ====================

export const LRFD_COMBINATIONS: { name: string; factors: Record<string, number> }[] = [
    { name: '1.4D', factors: { 'Dead': 1.4 } },
    { name: '1.2D + 1.6L', factors: { 'Dead': 1.2, 'Live': 1.6 } },
    { name: '1.2D + 1.6L + 0.5S', factors: { 'Dead': 1.2, 'Live': 1.6, 'Snow': 0.5 } },
    { name: '1.2D + 1.0W + L', factors: { 'Dead': 1.2, 'Wind': 1.0, 'Live': 1.0 } },
    { name: '1.2D + 1.0E + L', factors: { 'Dead': 1.2, 'Seismic': 1.0, 'Live': 1.0 } },
    { name: '0.9D + 1.0W', factors: { 'Dead': 0.9, 'Wind': 1.0 } },
    { name: '0.9D + 1.0E', factors: { 'Dead': 0.9, 'Seismic': 1.0 } },
];

export const ASD_COMBINATIONS: { name: string; factors: Record<string, number> }[] = [
    { name: 'D', factors: { 'Dead': 1.0 } },
    { name: 'D + L', factors: { 'Dead': 1.0, 'Live': 1.0 } },
    { name: 'D + 0.75L + 0.75S', factors: { 'Dead': 1.0, 'Live': 0.75, 'Snow': 0.75 } },
    { name: 'D + 0.6W', factors: { 'Dead': 1.0, 'Wind': 0.6 } },
    { name: 'D + 0.7E', factors: { 'Dead': 1.0, 'Seismic': 0.7 } },
    { name: '0.6D + 0.6W', factors: { 'Dead': 0.6, 'Wind': 0.6 } },
];

export const EUROCODE_COMBINATIONS: { name: string; factors: Record<string, number> }[] = [
    { name: '1.35G', factors: { 'Dead': 1.35 } },
    { name: '1.35G + 1.5Q', factors: { 'Dead': 1.35, 'Live': 1.5 } },
    { name: '1.35G + 1.5Q + 0.9W', factors: { 'Dead': 1.35, 'Live': 1.5, 'Wind': 0.9 } },
    { name: '1.35G + 1.5W + 1.05Q', factors: { 'Dead': 1.35, 'Wind': 1.5, 'Live': 1.05 } },
    { name: '1.0G + 1.0E + 0.3Q', factors: { 'Dead': 1.0, 'Seismic': 1.0, 'Live': 0.3 } },
];

// ==================== Structural Load Manager ====================

export class StructuralLoadManager {
    private loadCases: Map<string, LoadCase> = new Map();
    private loadCombinations: Map<string, LoadCombination> = new Map();

    constructor() {
        this.createDefaultLoadCases();
    }

    private createDefaultLoadCases(): void {
        // Create standard load cases
        const defaultCases: { name: string; type: LoadCaseType; selfWeight: number }[] = [
            { name: 'Dead', type: 'Dead', selfWeight: 1.0 },
            { name: 'Live', type: 'Live', selfWeight: 0 },
            { name: 'Wind X+', type: 'Wind', selfWeight: 0 },
            { name: 'Wind X-', type: 'Wind', selfWeight: 0 },
            { name: 'Wind Y+', type: 'Wind', selfWeight: 0 },
            { name: 'Wind Y-', type: 'Wind', selfWeight: 0 },
            { name: 'Seismic X', type: 'Seismic', selfWeight: 0 },
            { name: 'Seismic Y', type: 'Seismic', selfWeight: 0 },
            { name: 'Snow', type: 'Snow', selfWeight: 0 },
        ];

        for (const def of defaultCases) {
            this.createLoadCase(def.name, def.type, def.selfWeight);
        }
    }

    // ==================== Load Case Operations ====================

    public createLoadCase(name: string, type: LoadCaseType, selfWeightMultiplier: number = 0): LoadCase {
        const loadCase: LoadCase = {
            id: uuidv4(),
            name,
            type,
            selfWeightMultiplier,
            pointLoads: [],
            distributedLoads: [],
            areaLoads: []
        };
        this.loadCases.set(loadCase.id, loadCase);
        return loadCase;
    }

    public getLoadCase(id: string): LoadCase | undefined {
        return this.loadCases.get(id);
    }

    public getLoadCaseByName(name: string): LoadCase | undefined {
        for (const lc of this.loadCases.values()) {
            if (lc.name === name) return lc;
        }
        return undefined;
    }

    public getAllLoadCases(): LoadCase[] {
        return Array.from(this.loadCases.values());
    }

    // ==================== Point Load Operations ====================

    public addPointLoad(
        loadCaseId: string,
        nodeId: string,
        forces: { fx?: number; fy?: number; fz?: number; mx?: number; my?: number; mz?: number }
    ): PointLoad | null {
        const loadCase = this.loadCases.get(loadCaseId);
        if (!loadCase) return null;

        const pointLoad: PointLoad = {
            id: uuidv4(),
            nodeId,
            fx: forces.fx || 0,
            fy: forces.fy || 0,
            fz: forces.fz || 0,
            mx: forces.mx || 0,
            my: forces.my || 0,
            mz: forces.mz || 0
        };

        loadCase.pointLoads.push(pointLoad);
        return pointLoad;
    }

    // ==================== Distributed Load Operations ====================

    public addDistributedLoad(
        loadCaseId: string,
        memberId: string,
        options: {
            startMagnitude: number;
            endMagnitude?: number;
            direction?: 'Local' | 'Global';
            axis?: 'X' | 'Y' | 'Z';
            startPosition?: number;
            endPosition?: number;
        }
    ): DistributedLoad | null {
        const loadCase = this.loadCases.get(loadCaseId);
        if (!loadCase) return null;

        const distLoad: DistributedLoad = {
            id: uuidv4(),
            memberId,
            startMagnitude: options.startMagnitude,
            endMagnitude: options.endMagnitude ?? options.startMagnitude,
            direction: options.direction || 'Global',
            axis: options.axis || 'Z',
            startPosition: options.startPosition || 0,
            endPosition: options.endPosition || 1
        };

        loadCase.distributedLoads.push(distLoad);
        return distLoad;
    }

    // ==================== Area Load Operations ====================

    public addAreaLoad(
        loadCaseId: string,
        surfaceId: string,
        magnitude: number,
        direction: 'Normal' | 'Global Z' = 'Global Z'
    ): AreaLoad | null {
        const loadCase = this.loadCases.get(loadCaseId);
        if (!loadCase) return null;

        const areaLoad: AreaLoad = {
            id: uuidv4(),
            surfaceId,
            magnitude,
            direction
        };

        loadCase.areaLoads.push(areaLoad);
        return areaLoad;
    }

    // ==================== Load Combination Operations ====================

    public createLoadCombination(
        name: string,
        method: LoadCombinationMethod,
        factors: Record<string, number>,
        isEnvelope: boolean = false
    ): LoadCombination {
        const factorMap = new Map<string, number>();

        // Map load case names to IDs
        for (const [caseName, factor] of Object.entries(factors)) {
            const loadCase = this.getLoadCaseByName(caseName);
            if (loadCase) {
                factorMap.set(loadCase.id, factor);
            }
        }

        const combination: LoadCombination = {
            id: uuidv4(),
            name,
            method,
            factors: factorMap,
            isEnvelope
        };

        this.loadCombinations.set(combination.id, combination);
        return combination;
    }

    public generateStandardCombinations(method: LoadCombinationMethod): void {
        let combos: typeof LRFD_COMBINATIONS;

        switch (method) {
            case 'LRFD': combos = LRFD_COMBINATIONS; break;
            case 'ASD': combos = ASD_COMBINATIONS; break;
            case 'Eurocode': combos = EUROCODE_COMBINATIONS; break;
            default: return;
        }

        for (const combo of combos) {
            this.createLoadCombination(combo.name, method, combo.factors);
        }

        console.log(`Generated ${combos.length} ${method} load combinations`);
    }

    public getCombination(id: string): LoadCombination | undefined {
        return this.loadCombinations.get(id);
    }

    public getAllCombinations(): LoadCombination[] {
        return Array.from(this.loadCombinations.values());
    }

    // ==================== Combined Load Calculation ====================

    public calculateCombinedLoads(combinationId: string): {
        pointLoads: PointLoad[];
        distributedLoads: DistributedLoad[];
        areaLoads: AreaLoad[];
    } {
        const combination = this.loadCombinations.get(combinationId);
        if (!combination) {
            return { pointLoads: [], distributedLoads: [], areaLoads: [] };
        }

        const combinedPointLoads: Map<string, PointLoad> = new Map();
        const combinedDistLoads: DistributedLoad[] = [];
        const combinedAreaLoads: AreaLoad[] = [];

        for (const [loadCaseId, factor] of combination.factors) {
            const loadCase = this.loadCases.get(loadCaseId);
            if (!loadCase) continue;

            // Combine point loads by node
            for (const pl of loadCase.pointLoads) {
                const existing = combinedPointLoads.get(pl.nodeId);
                if (existing) {
                    existing.fx += pl.fx * factor;
                    existing.fy += pl.fy * factor;
                    existing.fz += pl.fz * factor;
                    existing.mx += pl.mx * factor;
                    existing.my += pl.my * factor;
                    existing.mz += pl.mz * factor;
                } else {
                    combinedPointLoads.set(pl.nodeId, {
                        id: uuidv4(),
                        nodeId: pl.nodeId,
                        fx: pl.fx * factor,
                        fy: pl.fy * factor,
                        fz: pl.fz * factor,
                        mx: pl.mx * factor,
                        my: pl.my * factor,
                        mz: pl.mz * factor
                    });
                }
            }

            // Scale distributed loads
            for (const dl of loadCase.distributedLoads) {
                combinedDistLoads.push({
                    ...dl,
                    id: uuidv4(),
                    startMagnitude: dl.startMagnitude * factor,
                    endMagnitude: dl.endMagnitude * factor
                });
            }

            // Scale area loads
            for (const al of loadCase.areaLoads) {
                combinedAreaLoads.push({
                    ...al,
                    id: uuidv4(),
                    magnitude: al.magnitude * factor
                });
            }
        }

        return {
            pointLoads: Array.from(combinedPointLoads.values()),
            distributedLoads: combinedDistLoads,
            areaLoads: combinedAreaLoads
        };
    }

    // ==================== Summary ====================

    public getSummary(): string {
        return `Load Cases: ${this.loadCases.size}, Combinations: ${this.loadCombinations.size}`;
    }
}
